import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

/* ─── SAME THEME TOKENS AS ABOUT / CONTACT / WAITLIST / PRIVACY ─── */
const THEMES = {
  light: {
    bg: "#ffffff",
    bgSub: "#f7f7f8",
    surface: "#f0f0f2",
    surfaceHover: "#e8e8eb",
    border: "#e3e3e6",
    borderStrong: "#c5c5ca",
    text: "#111111",
    textSub: "#5f5f6b",
    textMuted: "#a3a3b0",
    userBubble: "#1a1a1a",
    userText: "#ffffff",
    aiBubble: "#f7f7f8",
    aiBorder: "#e8e8eb",
    aiText: "#111111",
    accent: "#1a1a1a",
    accentText: "#ffffff",
    danger: "#d93025",
    success: "#188038",
    inputBg: "#f7f7f8",
    inputBorder: "#e3e3e6",
    sidebarBg: "#f7f7f8",
    sidebarBorder: "#e3e3e6",
    pillActive: "#1a1a1a",
    pillActiveText: "#ffffff",
    pillBg: "#ebebed",
    pillText: "#5f5f6b",
    shadow: "0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.05)",
    shadowMd: "0 4px 20px rgba(0,0,0,0.08)",
  },
  dark: {
    bg: "#212121",
    bgSub: "#2a2a2a",
    surface: "#303030",
    surfaceHover: "#3a3a3a",
    border: "#3a3a3a",
    borderStrong: "#4e4e4e",
    text: "#ececec",
    textSub: "#9e9e9e",
    textMuted: "#616161",
    userBubble: "#f0f0f0",
    userText: "#111111",
    aiBubble: "#2a2a2a",
    aiBorder: "#3a3a3a",
    aiText: "#ececec",
    accent: "#ececec",
    accentText: "#111111",
    danger: "#f28b82",
    success: "#81c995",
    inputBg: "#2a2a2a",
    inputBorder: "#3a3a3a",
    sidebarBg: "#1a1a1a",
    sidebarBorder: "#2e2e2e",
    pillActive: "#ececec",
    pillActiveText: "#111111",
    pillBg: "#303030",
    pillText: "#9e9e9e",
    shadow: "0 1px 3px rgba(0,0,0,0.3)",
    shadowMd: "0 4px 20px rgba(0,0,0,0.35)",
  },
};

const buildCSS = (t) => `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root {
    min-height: 100%;
    font-family: 'Google Sans Text', 'Google Sans', sans-serif;
    background: ${t.bg};
  }

  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: ${t.border}; border-radius: 8px; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .fade-up { animation: fadeUp 0.25s ease forwards; }

  .icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: none;
    background: transparent;
    color: ${t.textSub};
    cursor: pointer;
    transition: background 0.12s, color 0.12s;
    flex-shrink: 0;
  }

  .icon-btn:hover {
    background: ${t.surface};
    color: ${t.text};
  }

  .ghost-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: 1px solid ${t.border};
    border-radius: 12px;
    background: ${t.bg};
    color: ${t.text};
    padding: 13px 18px;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    font-family: 'Google Sans', sans-serif;
    transition: background 0.12s, border-color 0.12s;
    text-decoration: none;
  }

  .ghost-btn:hover {
    background: ${t.surface};
    border-color: ${t.borderStrong};
  }

  .primary-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: none;
    border-radius: 12px;
    background: ${t.accent};
    color: ${t.accentText};
    padding: 13px 18px;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    font-family: 'Google Sans', sans-serif;
    text-decoration: none;
    transition: opacity 0.12s, transform 0.12s;
  }

  .primary-btn:hover {
    opacity: .88;
    transform: translateY(-1px);
  }

  .material-icons-round {
    font-size: 20px;
    user-select: none;
    line-height: 1;
  }

  .footer {
    margin-top: 24px;
    background: ${t.bgSub};
    border: 1px solid ${t.border};
    border-radius: 18px;
    box-shadow: ${t.shadow};
  }

  .footer-inner {
    padding: 24px;
  }

  .footer-top {
    display: grid;
    grid-template-columns: 1.8fr 1fr 1fr 1fr 1fr;
    gap: 24px;
    margin-bottom: 22px;
  }

  .footer-logo {
    font-size: 22px;
    font-weight: 800;
    color: ${t.text};
    margin-bottom: 10px;
  }

  .footer-logo span {
    color: ${t.textSub};
  }

  .footer-brand p {
    margin: 0;
    font-size: 13px;
    color: ${t.textSub};
    line-height: 1.7;
  }

  .footer-col h4 {
    margin: 0 0 14px;
    font-size: 13px;
    font-weight: 700;
    color: ${t.text};
  }

  .footer-col a {
    display: block;
    margin-bottom: 10px;
    font-size: 13px;
    color: ${t.textSub};
    text-decoration: none;
    transition: color 0.12s ease;
  }

  .footer-col a:hover {
    color: ${t.text};
  }

  .footer-divider {
    height: 1px;
    background: ${t.border};
    margin-bottom: 16px;
  }

  .footer-bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .footer-bottom span {
    font-size: 12px;
    color: ${t.textMuted};
  }

  @media (max-width: 768px) {
    .footer {
      border-radius: 16px;
    }

    .footer-top {
      grid-template-columns: 1fr 1fr;
    }
  }

  @media (max-width: 520px) {
    .footer-top {
      grid-template-columns: 1fr;
    }
  }
`;

export default function Terms() {
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => localStorage.getItem("darkMode") === "true");
  const [mobile, setMobile] = useState(window.innerWidth <= 768);

  const T = dark ? THEMES.dark : THEMES.light;

  const sections = [
    {
      title: "1. Acceptance of Terms",
      content:
        "By accessing or using Omrali AI, you agree to be bound by these Terms of Service. If you do not agree with any part of these terms, you may not access or use our services.",
    },
    {
      title: "2. Use of Our Services",
      content:
        "Omrali AI provides conversational AI tools, APIs, and related services for businesses and developers. You agree to use our platform only for lawful purposes and in compliance with all applicable laws and regulations.",
    },
    {
      title: "3. Account Responsibilities",
      content:
        "You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must notify us immediately if you suspect unauthorized access or misuse.",
    },
    {
      title: "4. Acceptable Use",
      content:
        "You may not use Omrali AI to generate, distribute, or facilitate harmful, abusive, illegal, misleading, or malicious content. Misuse of our platform, including attempts to exploit, disrupt, or reverse engineer our systems, is strictly prohibited.",
    },
    {
      title: "5. Intellectual Property",
      content:
        "All rights, title, and interest in and to the Omrali AI platform, including software, branding, content, and infrastructure, remain the exclusive property of Omrali AI unless otherwise stated. You retain ownership of the content you submit, subject to the rights necessary for us to operate the service.",
    },
    {
      title: "6. Service Availability",
      content:
        "We aim to provide reliable and scalable access to our services, but we do not guarantee uninterrupted or error-free operation. We may modify, suspend, or discontinue parts of the platform at any time, with or without notice.",
    },
    {
      title: "7. Privacy and Data",
      content:
        "Your use of Omrali AI is also governed by our Privacy Policy. We are committed to protecting user data and handling information responsibly, but you remain responsible for ensuring that the data you submit is lawful and properly authorized.",
    },
    {
      title: "8. Limitation of Liability",
      content:
        "To the fullest extent permitted by law, Omrali AI shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from or related to your use of the platform, including loss of data, profits, or business opportunities.",
    },
    {
      title: "9. Termination",
      content:
        "We reserve the right to suspend or terminate your access to Omrali AI at our discretion if you violate these terms or if we believe your use poses a risk to our platform, users, or legal obligations.",
    },
    {
      title: "10. Changes to These Terms",
      content:
        "We may update these Terms of Service from time to time. Continued use of the platform after changes become effective constitutes your acceptance of the revised terms.",
    },
    {
      title: "11. Contact Us",
      content:
        "If you have any questions about these Terms of Service, please contact us through our official contact page or support channels.",
    },
  ];

  useEffect(() => {
    const fn = () => setMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    localStorage.setItem("darkMode", dark);
  }, [dark]);

  useEffect(() => {
    const styleId = "omrali-terms-theme";
    let styleTag = document.getElementById(styleId);

    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }

    styleTag.innerHTML = buildCSS(T);
  }, [T]);

  const Icon = ({ n, style }) => (
    <span className="material-icons-round" style={style}>
      {n}
    </span>
  );

  const Logo = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 7,
          background: T.accent,
          color: T.accentText,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: 14,
          fontFamily: "'Google Sans', sans-serif",
          flexShrink: 0,
        }}
      >
        O
      </div>
      <span
        style={{
          fontWeight: 700,
          fontSize: 15.5,
          color: T.text,
          fontFamily: "'Google Sans', sans-serif",
          letterSpacing: "-0.3px",
        }}
      >
        Omrali <span style={{ fontWeight: 400, color: T.textSub }}>AI</span>
      </span>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: T.bg, color: T.text }}>
      {/* Header */}
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: mobile ? "10px 14px" : "10px 20px",
          borderBottom: `1px solid ${T.border}`,
          background: T.bg,
          minHeight: 56,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button className="icon-btn" onClick={() => navigate(-1)} title="Back">
            <Icon n="arrow_back" />
          </button>
          <Logo />
        </div>

        <button className="icon-btn" onClick={() => setDark((d) => !d)} title="Toggle theme">
          <Icon n={dark ? "light_mode" : "dark_mode"} />
        </button>
      </header>

      {/* Main */}
      <main
        style={{
          maxWidth: 1040,
          margin: "0 auto",
          padding: mobile ? "18px 14px 28px" : "28px 24px 36px",
        }}
      >
        <div className="fade-up" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Hero */}
          <div
            style={{
              background: T.aiBubble,
              border: `1px solid ${T.aiBorder}`,
              borderRadius: 18,
              padding: mobile ? "18px" : "24px",
              boxShadow: T.shadow,
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: ".08em",
                color: T.textSub,
                marginBottom: 12,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: T.accent,
                  display: "inline-block",
                }}
              />
              Legal
            </div>

            <h1
              style={{
                fontSize: mobile ? 28 : 38,
                lineHeight: 1.1,
                fontWeight: 700,
                letterSpacing: "-0.03em",
                color: T.text,
                marginBottom: 12,
              }}
            >
              Terms of Service
            </h1>

            <p
              style={{
                color: T.textSub,
                fontSize: 14.5,
                lineHeight: 1.8,
                maxWidth: 760,
              }}
            >
              These Terms of Service govern your access to and use of Omrali AI’s
              platform, products, APIs, and related services.
            </p>

            <div
              style={{
                marginTop: 18,
                background: T.bg,
                border: `1px solid ${T.border}`,
                borderRadius: 14,
                padding: "16px 14px",
              }}
            >
              <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.8 }}>
                <strong style={{ color: T.text }}>Effective Date:</strong> April 8, 2026
                <br />
                <strong style={{ color: T.text }}>Last Updated:</strong> April 8, 2026
              </div>
            </div>
          </div>

          {/* Sections */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            {sections.map((section) => (
              <div
                key={section.title}
                style={{
                  background: T.bgSub,
                  border: `1px solid ${T.border}`,
                  borderRadius: 18,
                  padding: mobile ? "16px" : "22px",
                  boxShadow: T.shadow,
                }}
              >
                <h2
                  style={{
                    fontSize: mobile ? 18 : 20,
                    fontWeight: 700,
                    color: T.text,
                    marginBottom: 10,
                  }}
                >
                  {section.title}
                </h2>

                <p
                  style={{
                    color: T.textSub,
                    fontSize: 14,
                    lineHeight: 1.85,
                  }}
                >
                  {section.content}
                </p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div
            style={{
              background: T.aiBubble,
              border: `1px solid ${T.aiBorder}`,
              borderRadius: 18,
              padding: mobile ? "18px" : "22px",
              boxShadow: T.shadow,
              textAlign: "center",
            }}
          >
            <h2
              style={{
                fontSize: mobile ? 22 : 28,
                fontWeight: 700,
                color: T.text,
                marginBottom: 10,
              }}
            >
              Questions about our terms?
            </h2>

            <p
              style={{
                color: T.textSub,
                fontSize: 14,
                lineHeight: 1.7,
                maxWidth: 700,
                margin: "0 auto 18px",
              }}
            >
              Reach out to our team if you need clarification about usage, policies,
              or enterprise agreements.
            </p>

            <a href="/contact" className="primary-btn">
              <Icon n="gavel" />
              Contact Us
            </a>
          </div>

          {/* Footer */}
          <div className="footer fade-up">
            <div className="footer-inner">
              <div className="footer-top">
                <div className="footer-brand">
                  <div className="footer-logo">
                    Omrali <span>AI</span>
                  </div>
                  <p>
                    Next-generation conversational AI for businesses and developers.
                    Built with care, designed to scale.
                  </p>
                </div>

                <div className="footer-col">
                  <h4>Product</h4>
                  <a href="https://chat.omrali.com">Chat</a>
                </div>

                <div className="footer-col">
                  <h4>Company</h4>
                  <a href="/about">About us</a>
                  <a href="/waitlist">Beta</a>
                  <a href="/contact">Contact</a>
                </div>

                <div className="footer-col">
                  <h4>Legal</h4>
                  <a href="/privacy">Privacy policy</a>
                  <a href="/terms">Terms of service</a>
                </div>

                <div className="footer-col">
                  <h4>Social</h4>
                  <a href="https://www.instagram.com/omrali.ai/">Instagram</a>
                  <a href="https://twitter.com/omrali_ai">Twitter</a>
                </div>
              </div>

              <div className="footer-divider" />

              <div className="footer-bottom">
                <span>© 2026 Omrali AI. All rights reserved.</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}